import { useState } from "react";
import { Users, DoorOpen } from "lucide-react";

export const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri"];
const todayKey = () => ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][new Date().getDay()];
const hm = () => { const n = new Date(); return n.getHours() * 60 + n.getMinutes(); };
const toMin = (t) => { const m = /^(\d{1,2}):(\d{2})$/.exec(t || ""); return m ? Number(m[1]) * 60 + Number(m[2]) : null; };
export const isNow = (c, day) => day === todayKey() && toMin(c.start_time) !== null && toMin(c.end_time) !== null && hm() >= toMin(c.start_time) && hm() < toMin(c.end_time);
const periodKey = (p) => { const m = /\d+/.exec(p || ""); return m ? [0, parseInt(m[0], 10), p] : [1, 0, p || ""]; };
export const sortByPeriod = (list) => [...list].sort((a, b) => { const x = periodKey(a.period), y = periodKey(b.period); return x[0] - y[0] || x[1] - y[1] || String(x[2]).localeCompare(String(y[2])) || a.name.localeCompare(b.name); });

/** Weekly timetable (Mon–Fri) for a list of class sections. Day tabs on mobile, columns on wide screens. */
export function Timetable({ classes, onSelect, showTeacher = false, testid = "timetable", compact = false }) {
  const [day, setDay] = useState(WEEKDAYS.includes(todayKey()) ? todayKey() : "Mon");
  const byDay = Object.fromEntries(WEEKDAYS.map((d) => [d, sortByPeriod(classes.filter((c) => (c.days || WEEKDAYS).includes(d)))]));
  const Cell = ({ c, d }) => (
    <button type="button" onClick={() => onSelect?.(c)} data-testid={`${testid}-cell-${c.id}`}
      className={`w-full rounded-lg border bg-white p-2.5 text-left hover:border-primary/50 hover:shadow-sm transition ${isNow(c, d) ? "border-emerald-400 ring-2 ring-emerald-200" : "border-slate-200"}`}>
      <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-widest text-primary">
        <span>{c.period || "Homeroom"}{c.start_time ? <span className="ml-1 font-medium normal-case tracking-normal text-slate-400">{c.start_time}–{c.end_time}</span> : null}</span>
        {isNow(c, d) && <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[9px] text-emerald-700" data-testid={`${testid}-now-${c.id}`}>Now</span>}
      </div>
      <div className="text-sm font-semibold text-slate-900 leading-tight truncate">{c.name}</div>
      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-slate-500">
        {showTeacher && c.teacher_name && <span className="truncate">{c.teacher_name}</span>}
        {c.room && <span className="inline-flex items-center gap-0.5"><DoorOpen className="h-3 w-3" /> Rm {c.room}</span>}
        <span className="inline-flex items-center gap-0.5"><Users className="h-3 w-3" /> {c.student_count}</span>
      </div>
    </button>
  );
  return (
    <div data-testid={testid}>
      <div className={`grid gap-2 ${compact ? "" : "md:hidden"} grid-cols-5 mb-3`} data-testid={`${testid}-day-tabs`}>
        {WEEKDAYS.map((d) => (
          <button key={d} type="button" onClick={() => setDay(d)} data-testid={`${testid}-day-${d}`}
            className={`h-9 rounded-lg text-xs font-semibold transition ${day === d ? "bg-primary text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"} ${d === todayKey() && day !== d ? "ring-2 ring-primary/30" : ""}`}>{d}{byDay[d].length ? <span className="ml-1 opacity-70">({byDay[d].length})</span> : null}</button>
        ))}
      </div>
      <div className={compact ? "" : "md:hidden"}>
        <div className="space-y-2" data-testid={`${testid}-day-list`}>
          {byDay[day].length === 0 && <div className="rounded-lg border border-dashed border-slate-200 p-6 text-center text-sm text-slate-400">Nothing scheduled on {day}.</div>}
          {byDay[day].map((c) => <Cell key={c.id} c={c} d={day} />)}
        </div>
      </div>
      {!compact && (
        <div className="hidden md:grid grid-cols-5 gap-3" data-testid={`${testid}-week-grid`}>
          {WEEKDAYS.map((d) => (
            <div key={d} className="min-w-0">
              <div className={`text-xs font-bold uppercase tracking-widest mb-2 ${d === todayKey() ? "text-primary" : "text-slate-500"}`}>{d}{d === todayKey() ? " · today" : ""}</div>
              <div className="space-y-2">
                {byDay[d].length === 0 && <div className="rounded-lg border border-dashed border-slate-200 p-4 text-center text-xs text-slate-400">—</div>}
                {byDay[d].map((c) => <Cell key={c.id} c={c} d={d} />)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Mon–Fri checkbox row for class forms. */
export function DaysPicker({ value, onChange, testid = "days" }) {
  const days = value?.length ? value : WEEKDAYS;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {WEEKDAYS.map((d) => {
        const on = days.includes(d);
        return (
          <button key={d} type="button" onClick={() => onChange(on ? days.filter((x) => x !== d) : WEEKDAYS.filter((x) => x === d || days.includes(x)))} data-testid={`${testid}-${d}`}
            className={`h-8 px-3 rounded-full text-xs font-semibold transition ${on ? "bg-primary text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>{d}</button>
        );
      })}
    </div>
  );
}
